import * as dayjs from 'dayjs';
import T from 'i18n-react';
import { FieldValue } from './form/types';
import { ENV, IBrowserInfo, IIntegrationUiOptions, IRule } from './types';

export const getEnv = (): ENV => {
  return (window as any).salesmatchEnv;
};

declare const window: any;

export const postMessage = (source: string, message: string, postData = {}) => {
  window.parent.postMessage(
    {
      fromErxes: true,
      source,
      message,
      ...postData,
    },
    '*'
  );
};

type RequestBrowserInfoParams = {
  source: string;
  postData?: {};
  callback: (browserInfo: IBrowserInfo) => void;
};

export type LogicParams = {
  fieldId: string;
  operator: string;
  validation?: string;
  logicValue: FieldValue;
  fieldValue?: FieldValue;
  type?: string;
};

export const requestBrowserInfo = ({
  source,
  postData = {},
  callback,
}: RequestBrowserInfoParams) => {
  postMessage(source, 'requestingBrowserInfo', postData);

  window.addEventListener('message', (event: any) => {
    const data = event.data || {};
    const { fromPublisher, message, browserInfo } = data;

    if (
      fromPublisher &&
      source === data.source &&
      message === 'sendingBrowserInfo'
    ) {
      callback(browserInfo);
    }
  });
};

const setDayjsLocale = (code: string) => {
  import('dayjs/locale/' + code + '.js')
    .then(() => dayjs.locale(code))
    .catch(() => dayjs.locale('es'));
};

export const setLocale = (code?: string, callBack?: () => void) => {
  return import(`../locales/${code}.json`)
    .then((translations) => {
      T.setTexts(translations);
      setDayjsLocale(code || 'es');

      if (callBack) {
        callBack();
      }
    })
    .catch((e) => console.log(e)); // tslint:disable-line
};

export const __ = (msg: string) => {
  return T.translate(msg);
};

export const scrollTo = (element: any, to: number, duration: number) => {
  const start = element.scrollTop;
  const change = to - start;
  const increment = 20;
  let currentTime = 0;

  const animateScroll = () => {
    const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
      t /= d / 2;
      if (t < 1) {
        return (c / 2) * t * t + b;
      }
      t--;
      return (-c / 2) * (t * (t - 2) - 1) + b;
    };

    currentTime += increment;

    const val = easeInOutQuad(currentTime, start, change, duration);

    if (element && element.scrollTop !== undefined) {
      element.scrollTop = val;
    }

    if (currentTime < duration) {
      setTimeout(animateScroll, increment);
    }
  };

  animateScroll();
};

export const makeClickableLink = (
  selector: string,
  uiOptions?: IIntegrationUiOptions,
  colorOverride?: string,
) => {
  const nodes = Array.from(document.querySelectorAll(selector));
  const color = colorOverride || uiOptions?.linkForeground;

  nodes.forEach((node) => {
    node.setAttribute('target', '__blank');
    node.setAttribute('style', `color: ${color};`)
  });
};

// check if valid url
export const isValidURL = (url: string) => {
  try {
    return Boolean(new URL(url));
  } catch (e) {
    return false;
  }
};

/**
 * Request to get file's URL for view and download
 * @param {String} - value
 * @return {String} - URL
 */
export const readFile = (value: string): string => {
  const { API_URL } = getEnv();

  if (!value || isValidURL(value) || value.includes('http')) {
    return value;
  }

  return `${API_URL}/read-file?key=${value}`;
};

export const checkRule = (rule: IRule, browserInfo: IBrowserInfo) => {
  const { language, url, city, country } = browserInfo || ({} as IBrowserInfo);
  const { value, kind, condition } = rule;
  const ruleValue: any = value;

  let valueToTest: any;

  if (kind === 'browserLanguage') {
    valueToTest = language;
  }

  if (kind === 'currentPageUrl') {
    valueToTest = url;
  }

  if (kind === 'city') {
    valueToTest = city;
  }

  if (kind === 'country') {
    valueToTest = country;
  }

  // is
  if (condition === 'is' && valueToTest !== ruleValue) {
    return false;
  }

  // isNot
  if (condition === 'isNot' && valueToTest === ruleValue) {
    return false;
  }

  // isUnknown
  if (condition === 'isUnknown' && valueToTest) {
    return false;
  }

  // hasAnyValue
  if (condition === 'hasAnyValue' && !valueToTest) {
    return false;
  }

  // startsWith
  if (
    condition === 'startsWith' &&
    valueToTest &&
    !valueToTest.startsWith(ruleValue)
  ) {
    return false;
  }

  // endsWith
  if (
    condition === 'endsWith' &&
    valueToTest &&
    !valueToTest.endsWith(ruleValue)
  ) {
    return false;
  }

  // contains
  if (
    condition === 'contains' &&
    valueToTest &&
    !valueToTest.includes(ruleValue)
  ) {
    return false;
  }

  // greaterThan
  if (condition === 'greaterThan' && valueToTest < parseInt(ruleValue, 10)) {
    return false;
  }

  if (condition === 'lessThan' && valueToTest > parseInt(ruleValue, 10)) {
    return false;
  }

  if (condition === 'doesNotContain' && valueToTest.includes(ruleValue)) {
    return false;
  }

  return true;
};

export const checkRules = (
  rules: IRule[],
  browserInfo: IBrowserInfo
): boolean => {
  let passedAllRules = true;

  for (const rule of rules) {
    const result = checkRule(rule, browserInfo);

    if (result === false) {
      passedAllRules = false;
    }
  }

  return passedAllRules;
};

export const striptags = (htmlString: string) => {
  const _div = document.createElement('div');
  let _text = '';

  _div.innerHTML = htmlString;
  _text = _div.textContent ? _div.textContent.trim() : '';
  _text = _text.replace(/\</g, '&lt;').replace(/\>/g, '&gt;');
  return _text;
};

export const fixErrorMessage = (msg: string) =>
  msg.replace('GraphQL error: ', '');

export const newLineToBr = (content: string) => {
  return content.replace(/\r\n|\r|\n/g, '<br />');
};

export const urlify = (text: string) => {
  if (!text) {
    return text;
  }

  // 1. Email addresses (must be checked before URLs to avoid partial matches)
  const emailRegex = /\b[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}\b(?![^<>]*>|[^"]*?<\/a)/g;

  // 2. URLs with explicit protocol — allow %-encoded chars and parentheses
  const explicitUrlRegex = /https?:\/\/[^\s<>"']+(?![^<>]*>|[^"]*?<\/a)/g;

  // 3. URLs without protocol — require a known TLD to avoid matching numbers like "20.000"
  const knownTlds = 'com|org|net|edu|gov|io|co|info|biz|me|tv|us|uk|de|fr|es|it|nl|be|at|ch|ru|br|jp|cn|au|ca|in|cat|app|dev|xyz|tech|online|site|store|shop|blog|art|ai';
  const implicitUrlRegex = new RegExp(
    `\\b([a-zA-Z0-9][a-zA-Z0-9\\-]*\\.)+(?:${knownTlds})(?:[/][^\\s<>"']*)?(?![^<>]*>|[^"]*?<\\/a)`,
    'gi'
  );

  // Process in order: first protect emails, then explicit URLs, then implicit URLs
  // Use a placeholder approach to avoid double-replacement
  const placeholders: string[] = [];
  const placeholder = (html: string) => {
    const idx = placeholders.length;
    placeholders.push(html);
    return `\x00URLIFY${idx}\x00`;
  };

  // Pass 1: emails
  let result = text.replace(emailRegex, (email) =>
    placeholder(`<a style="color: #777;" href="mailto:${email}">${email}</a>`)
  );

  // Pass 2: explicit URLs (http/https)
  result = result.replace(explicitUrlRegex, (url) => {
    // Strip trailing punctuation that's likely not part of the URL
    const trailingMatch = url.match(/[.),:;!?]+$/);
    let cleanUrl = url;
    let trailing = '';
    if (trailingMatch) {
      // Keep trailing chars that have matching openers in the URL (e.g. parentheses)
      const trail = trailingMatch[0];
      const openParens = (cleanUrl.match(/\(/g) || []).length;
      const closeParens = (cleanUrl.match(/\)/g) || []).length;
      if (trail.includes(')') && openParens >= closeParens) {
        // Parens are balanced, keep them (common in Wikipedia URLs)
      } else {
        cleanUrl = url.slice(0, -trail.length);
        trailing = trail;
      }
    }
    return placeholder(`<a style="color: #777;" href="${cleanUrl}" target="_blank">${cleanUrl}</a>`) + trailing;
  });

  // Pass 3: implicit URLs (no protocol)
  result = result.replace(implicitUrlRegex, (url) => {
    // Strip trailing punctuation
    const trailingMatch = url.match(/[.),:;!?]+$/);
    let cleanUrl = url;
    let trailing = '';
    if (trailingMatch) {
      cleanUrl = url.slice(0, -trailingMatch[0].length);
      trailing = trailingMatch[0];
    }
    return placeholder(`<a style="color: #777;" href="http://${cleanUrl}" target="_blank">${cleanUrl}</a>`) + trailing;
  });

  // Restore placeholders
  result = result.replace(/\x00URLIFY(\d+)\x00/g, (_, idx) => placeholders[Number(idx)]);

  return result;
};

export const checkLogicFulfilled = (logics: LogicParams[]) => {
  const values: { [key: string]: boolean } = {};

  for (const logic of logics) {
    const {
      fieldId,
      operator,
      logicValue,
      fieldValue,
      validation,
      type,
    } = logic;
    const key = `${fieldId}_${logicValue}`;
    values[key] = false;

    // if fieldValue is set
    if (operator === 'hasAnyValue') {
      if (fieldValue) {
        values[key] = true;
      } else {
        values[key] = false;
      }
    }

    // if fieldValue is not set
    if (operator === 'isUnknown') {
      if (!fieldValue) {
        values[key] = true;
      } else {
        values[key] = false;
      }
    }

    // if fieldValue equals logic value
    if (operator === 'is') {
      if (logicValue === fieldValue) {
        values[key] = true;
      } else {
        values[key] = false;
      }
    }

    // if fieldValue not equal to logic value
    if (operator === 'isNot') {
      if (logicValue !== fieldValue) {
        values[key] = true;
      } else {
        values[key] = false;
      }
    }

    if (validation === 'number') {
      // if number value: is greater than
      if (operator === 'greaterThan' && fieldValue) {
        if (Number(fieldValue) > Number(logicValue)) {
          values[key] = true;
        } else {
          values[key] = false;
        }
      }

      // if number value: is less than
      if (operator === 'lessThan' && fieldValue) {
        if (Number(fieldValue) < Number(logicValue)) {
          values[key] = true;
        } else {
          values[key] = false;
        }
      }
    }

    if (typeof logicValue === 'string') {
      // if string value contains logicValue
      if (operator === 'contains') {
        if (String(fieldValue).includes(logicValue)) {
          values[key] = true;
        } else {
          values[key] = false;
        }
      }

      // if string value does not contain logicValue
      if (operator === 'doesNotContain') {
        if (!String(fieldValue).includes(logicValue)) {
          values[key] = true;
        } else {
          values[key] = false;
        }
      }

      // if string value startsWith logicValue
      if (operator === 'startsWith') {
        if (String(fieldValue).startsWith(logicValue)) {
          values[key] = true;
        } else {
          values[key] = false;
        }
      }

      // if string value endsWith logicValue
      if (operator === 'endsWith') {
        if (!String(fieldValue).endsWith(logicValue)) {
          values[key] = true;
        } else {
          values[key] = false;
        }
      }
    }

    if (validation && validation.includes('date')) {
      const dateValueToCheck = new Date(String(fieldValue));
      const logicDateValue = new Date(String(logicValue));

      // date is greather than
      if (operator === 'dateGreaterThan') {
        if (dateValueToCheck > logicDateValue) {
          values[key] = true;
        } else {
          values[key] = false;
        }
      }

      // date is less than
      if (operator === 'dateLessThan') {
        if (logicDateValue > dateValueToCheck) {
          values[key] = true;
        } else {
          values[key] = false;
        }
      }
    }

    if (type === 'check') {
      if (
        fieldValue &&
        typeof fieldValue === 'string' &&
        typeof logicValue === 'string'
      ) {
        if (operator === 'isNot') {
          if (!fieldValue.includes(logicValue)) {
            values[key] = true;
          } else {
            values[key] = false;
          }
        }

        if (operator === 'is') {
          if (fieldValue.includes(logicValue)) {
            values[key] = true;
          } else {
            values[key] = false;
          }
        }
      }
    }
  }

  const result = [];

  for (const key of Object.keys(values)) {
    result.push(values[key]);
  }

  if (result.filter((val) => !val).length === 0) {
    return true;
  }

  return false;
};

export const loadMapApi = (apiKey: string, locale?: string) => {
  if (!apiKey) {
    return '';
  }

  const mapsURL = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry,places&language=${locale ||
    'en'}&v=quarterly`;
  const scripts: any = document.getElementsByTagName('script');
  // Go through existing script tags, and return google maps api tag when found.
  for (const script of scripts) {
    if (script.src.indexOf(mapsURL) === 0) {
      return script;
    }
  }

  const googleMapScript = document.createElement('script');
  googleMapScript.src = mapsURL;
  googleMapScript.async = true;
  googleMapScript.defer = true;
  window.document.body.appendChild(googleMapScript);

  return googleMapScript;
};

function hexToRgb(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return { r, g, b };
}

// Function to convert RGB to Hex
function rgbToHex(r: any, g: any, b: any) {
  const toHex = (x: any) => x.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function adjustBrightness(hex: string, factor: any) {
  let { r, g, b } = hexToRgb(hex);

  // Adjust each RGB value based on the factor
  r = Math.min(255, Math.max(0, r + factor));
  g = Math.min(255, Math.max(0, g + factor));
  b = Math.min(255, Math.max(0, b + factor));

  return rgbToHex(r, g, b);
}